<template>
    <div v-if="loaded" class="nevs-content">
        <div class="nevs-content-top-buttons">
            <template v-if="!readOnly">
                <NevsButton class="success" @click="saveClick()"><i class="fa-solid fa-floppy-disk"></i>
                    {{ $LANG.Get('buttons.save') }}
                </NevsButton>&nbsp;
            </template>
            <NevsButton class="error" @click="backClick"><i class="fa-solid fa-arrow-left"></i>
                {{ $LANG.Get('buttons.back') }}
            </NevsButton>
        </div>

        <div v-if="readOnly" class="notification-sent-banner">
            <i class="fa-solid fa-circle-check"></i> {{ $LANG.Get('labels.notificationSentBanner') }}
        </div>
        <template v-else>
            <div class="notification-disclaimer">
                <i class="fa-solid fa-circle-info"></i> {{ channelDisclaimerText }}
            </div>
            <div v-if="!isMobile" class="notification-disclaimer">
                <i class="fa-solid fa-circle-info"></i> {{ subjectDisclaimerText }}
            </div>
        </template>

        <div class="notification-first-row">
        <NevsCard class="form form-float">
            <!-- Hidden (not removed) while the members app is not yet on the app stores; the channel stays e-mail. -->
            <NevsSelect v-show="!$store.state.settings.MOBILE_TEMP_DISABLED" v-model="channel" :options="channelOptions" :readonly="readOnly"
                        :label="'*' + $LANG.Get('fields.notificationChannel') + ':'"></NevsSelect>
            <NevsTextField v-model="notification.name" :error="validation.name" :readonly="readOnly"
                           :label="'*' + $LANG.Get('fields.internalName') + ':'"></NevsTextField>
            <NevsTextField v-model="notification.subject" :error="validation.subject" :readonly="readOnly"
                           :label="subjectLabel"></NevsTextField>
            <NevsCheckbox v-if="!readOnly" v-model="sendNow" :label="$LANG.Get('fields.sendNow')"
                          class="notification-sendnow-field"></NevsCheckbox>
            <div v-if="sendNowBlockedByLimit" class="notification-sendnow-warning">
                <i class="fa-solid fa-triangle-exclamation"></i> {{ sendNowOverLimitText }}
            </div>
            <template v-if="!sendNow">
                <NevsDateField v-model="sendDate" :error="validation.time" :readonly="readOnly"
                               :label="(notification.active ? '*' : '') + $LANG.Get('fields.sendTime') + ':'" class="notification-half"></NevsDateField>
                <NevsMaskedField v-model="sendTime" :mask="'NN:NN'" :readonly="readOnly" class="notification-half"></NevsMaskedField>
                <div class="nevs-clear-float"></div>
                <NevsCheckbox v-model="notification.active" :readonly="readOnly" :label="$LANG.Get('fields.activeF')"
                              class="notification-active-field"></NevsCheckbox>
            </template>
            <div class="notification-sent-field">
                {{ $LANG.Get('fields.sent') }}:
                <span :class="notification.sent ? 'sent-pill sent-pill-yes' : 'sent-pill sent-pill-no'">
                    {{ notification.sent ? $LANG.Get('labels.yes') : $LANG.Get('labels.no') }}
                </span>
            </div>
        </NevsCard>

        <!-- Attachments and images in the content are hidden for now (uploadsEnabled). A notification that already has
             attachments still lists them, so they can be seen and removed. -->
        <NevsCard v-if="uploadsEnabled || notification.attachments.length > 0" class="form form-float">
            <h3 class="notification-heading">{{ $LANG.Get('fields.attachments') }}</h3>
            <!-- A mobile notification cannot carry attachments. Ones added before switching stay listed so they can
                 be removed (or the channel switched back), rather than silently disappearing. -->
            <div v-if="isMobile && !readOnly" class="notification-disclaimer notification-attachments-disclaimer">
                <i class="fa-solid fa-circle-info"></i> {{ $LANG.Get('labels.mobileNoAttachments') }}
            </div>
            <NevsMultiUpload v-if="uploadsEnabled && !readOnly && !isMobile" @uploaded="attachmentsUploaded"></NevsMultiUpload>
            <div v-if="validation.attachments" class="box-error nevs-text-error">{{ validation.attachments }}</div>
            <div class="notification-attachments">
                <div v-if="notification.attachments.length === 0" class="notification-attachments-empty">
                    {{ $LANG.Get('labels.noAttachments') }}
                </div>
                <div v-for="(attachment, key) in notification.attachments" :key="key" class="attachment-row">
                    <i class="fa-solid fa-file attachment-row-icon"></i>
                    <div class="attachment-row-name" :title="attachment.file.name">{{ attachment.file.name }}</div>
                    <div class="attachment-row-actions">
                        <span :title="$LANG.Get('tooltips.downloadFile')" class="attachment-row-button"
                              @click="downloadAttachment(attachment)"><i class="fa-solid fa-download"></i></span>
                        <span v-if="!readOnly" :title="$LANG.Get('tooltips.delete')"
                              class="attachment-row-button attachment-row-button-delete"
                              @click="removeAttachment(key)"><i class="fa-solid fa-trash"></i></span>
                    </div>
                </div>
            </div>
        </NevsCard>
        </div>

        <NevsCard class="notification-content-card">
            <h3 class="notification-heading">{{ $LANG.Get('fields.content') }}</h3>
            <!-- The members app shows plain text, so a mobile notification is written as plain text: what staff type
                 is exactly what members see. -->
            <NevsTextArea v-if="isMobile" v-model="notification.content" :error="validation.content" :readonly="readOnly"
                          class="notification-mobile-content"></NevsTextArea>
            <NevsWysiwyg v-else v-model="notification.content" :error="validation.content" :readonly="readOnly"
                         :allow-images="uploadsEnabled"></NevsWysiwyg>
        </NevsCard>

        <NevsCard class="notification-recipients-card">
            <h3 class="notification-heading">{{ $LANG.Get('labels.recipients') }}</h3>
            <div v-if="isMobile && !readOnly && reach !== null" class="notification-reach">
                <div class="notification-reach-title">
                    <i class="fa-solid fa-mobile-screen"></i> {{ $LANG.Get('labels.notificationReachTitle') }}
                </div>
                <div class="notification-reach-row">
                    <span class="notification-reach-number">{{ reach.filtered }}</span>
                    {{ $LANG.Get('labels.notificationReachSelected') }}
                </div>
                <div class="notification-reach-row">
                    <span class="notification-reach-number">{{ reach.with_app_account }}</span>
                    {{ $LANG.Get('labels.notificationReachInApp') }}
                </div>
                <div class="notification-reach-row">
                    <span class="notification-reach-number">{{ reach.with_active_device }}</span>
                    {{ $LANG.Get('labels.notificationReachWithDevice') }}
                </div>
                <div class="notification-reach-row">
                    <span class="notification-reach-number">{{ reach.notifications_disabled }}</span>
                    {{ $LANG.Get('labels.notificationReachDisabled') }}
                </div>
                <div class="notification-reach-note">{{ $LANG.Get('labels.notificationReachDisclaimer') }}</div>
            </div>
            <div v-if="isMobile && readOnly && notification.report" class="notification-reach">
                <div class="notification-reach-title">
                    <i class="fa-solid fa-mobile-screen"></i> {{ $LANG.Get('labels.notificationReportTitle') }}
                </div>
                <div class="notification-reach-row">
                    <span class="notification-reach-number">{{ notification.report.recipients }}</span>
                    {{ $LANG.Get('labels.notificationReportRecipients') }}
                </div>
                <div class="notification-reach-row">
                    <span class="notification-reach-number">{{ notification.report.pushed }}</span>
                    {{ $LANG.Get('labels.notificationReportPushed') }}
                </div>
                <div class="notification-reach-row">
                    <span class="notification-reach-number">{{ notification.report.read }}</span>
                    {{ $LANG.Get('labels.notificationReportRead') }}
                </div>
            </div>
            <div v-if="validation.persons" class="box-error nevs-text-error">{{ validation.persons }}</div>
            <div v-if="!readOnly" class="notification-scope-row">
                <NevsCheckbox v-model="scopeAllActive"
                              :label="$LANG.Get('fields.recipientScopeAllActive')"></NevsCheckbox>
                <NevsCheckbox v-model="scopeActiveMembership"
                              :label="$LANG.Get('fields.recipientScopeActiveMembership')"></NevsCheckbox>
                <NevsCheckbox v-model="scopeMembership"
                              :label="$LANG.Get('fields.recipientScopeMembership')"></NevsCheckbox>
            </div>
            <div v-if="!readOnly && scopeMembership" class="notification-scope-membership-row">
                <NevsSelect :nullable="true" v-model="notification.recipient_membership_id"
                            :ajax="'select/memberships'" :error="validation.recipient_membership_id"
                            :label="$LANG.Get('fields.membership') + ':'"
                            class="notification-add-field"></NevsSelect>
            </div>
            <div v-if="usingScope" class="notification-disclaimer notification-scope-disclaimer">
                <i class="fa-solid fa-circle-info"></i> {{ scopeDisclaimerText }}
            </div>
            <div v-if="!readOnly && !usingScope" class="notification-add-row">
                <NevsSelect :nullable="true" v-model="selectedPersonId" :ajax="'select/persons'"
                            :label="$LANG.Get('fields.person') + ':'" class="notification-add-field"></NevsSelect>
                <NevsSelect :nullable="true" v-model="selectedGroupId" :ajax="'select/groups'"
                            :label="$LANG.Get('labels.addFromGroup') + ':'" class="notification-add-field"></NevsSelect>
                <NevsSelect :nullable="true" v-model="selectedSessionId" :ajax="'select/sessions'"
                            :label="$LANG.Get('labels.addFromSession') + ':'" class="notification-add-field"></NevsSelect>
            </div>
            <table v-if="!usingScope" class="notification-persons-table">
                <tbody>
                    <tr v-for="(person, key) in notification.persons" :key="person.person_id">
                        <td>
                            {{ person.person_name }}
                            <span v-if="person.sent === true"
                                  :title="$LANG.Get('labels.personNotified')"
                                  class="notification-person-sent">
                                <i class="fa-solid fa-circle-check"></i>
                            </span>
                            <span v-if="!personNotifiable(person)"
                                  :title="personWontBeNotifiedText"
                                  class="notification-person-warning">
                                <i class="fa-solid fa-triangle-exclamation"></i>
                            </span>
                        </td>
                        <td v-if="!readOnly" class="notification-persons-table-action">
                            <span :title="$LANG.Get('tooltips.delete')" class="notification-button"
                                  @click="removePerson(key)"><i class="fa-solid fa-trash"></i></span>
                        </td>
                    </tr>
                    <tr v-if="notification.persons.length === 0">
                        <td colspan="2" class="notification-persons-empty">{{ $LANG.Get('labels.noRecipients') }}</td>
                    </tr>
                </tbody>
            </table>
        </NevsCard>
    </div>
</template>

<script>
import Notification from '@/models/Notification.js';
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsTextArea from "@/components/nevs/NevsTextArea.vue";
import NevsDateField from "@/components/nevs/NevsDateField.vue";
import NevsMaskedField from "@/components/nevs/NevsMaskedField.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsWysiwyg from "@/components/nevs/NevsWysiwyg.vue";
import NevsMultiUpload from "@/components/nevs/NevsMultiUpload.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";

export default {
    name: "EntityNotification",
    components: {NevsCheckbox, NevsMultiUpload, NevsWysiwyg, NevsSelect, NevsMaskedField, NevsDateField, NevsTextArea, NevsTextField, NevsButton, NevsCard},
    data() {
        return {
            loaded: false,
            mode: this.$route.params.id === '0' ? 'add' : 'edit',
            notification: new Notification({}),
            validation: new Notification({}, true),
            sendDate: '',
            sendTime: '',
            sendNow: false,
            quota: null,
            reach: null,
            reachTimer: null,
            selectedPersonId: null,
            selectedGroupId: null,
            selectedSessionId: null,
            scopeAllActive: false,
            scopeActiveMembership: false,
            scopeMembership: false,
            // Attachment uploads and images in the content, hidden for now. Set to true to bring them back.
            uploadsEnabled: false
        }
    },
    computed: {
        readOnly() {
            return this.notification.sent === true;
        },
        isMobile() {
            return this.notification.channel === 'mobile';
        },
        /**
         * The channel, with the content carried over when it changes: the e-mail editor holds HTML and the mobile
         * text box plain text, so switching converts rather than showing markup or losing line breaks. Setting
         * the same value again (as a select may do when it mounts) changes nothing.
         */
        channel: {
            get() {
                return this.notification.channel;
            },
            set(value) {
                if (value === this.notification.channel || (value !== 'email' && value !== 'mobile')) return;
                if (value === 'mobile') {
                    this.notification.content = this.htmlToText(this.notification.content);
                } else {
                    this.notification.content = this.textToHtml(this.notification.content);
                }
                this.notification.channel = value;
                this.validation.subject = '';
                this.validation.content = '';
                this.validation.attachments = '';
                this.validation.persons = '';
            }
        },
        channelOptions() {
            return [
                {value: 'email', label: this.$LANG.Get('notificationChannels.email')},
                {value: 'mobile', label: this.$LANG.Get('notificationChannels.mobile')}
            ];
        },
        subjectLabel() {
            return this.isMobile
                ? '*' + this.$LANG.Get('fields.notificationTitle') + ':'
                : this.$LANG.Get('fields.emailSubject') + ':';
        },
        overLimit() {
            return this.quota !== null && this.quota.sent >= this.quota.limit;
        },
        sendNowBlockedByLimit() {
            // The monthly limit counts e-mails only.
            return this.sendNow && this.overLimit && !this.isMobile;
        },
        recipientScope() {
            if (this.scopeAllActive) return 'all_active';
            if (this.scopeActiveMembership) return 'active_membership';
            if (this.scopeMembership) return 'membership';
            return '';
        },
        usingScope() {
            return this.recipientScope !== '';
        },
        hasEligibleRecipient() {
            // A scope is resolved by the server at send time, so whether it covers anybody is the server's
            // answer to give (it refuses an empty one with `no_recipients`); a hand-picked list is checked here.
            if (this.usingScope) return true;
            return this.notification.persons.some((person) => this.personNotifiable(person));
        },
        sendNowOverLimitText() {
            // While the members app is not yet on the app stores, the text does not point to it as the way around.
            return this.$LANG.Get(this.$store.state.settings.MOBILE_TEMP_DISABLED ? 'labels.sendNowOverLimitNoMobile' : 'labels.sendNowOverLimit');
        },
        channelDisclaimerText() {
            return this.$LANG.Get(this.isMobile ? 'labels.mobileNotificationDisclaimer' : 'labels.notificationDisclaimer');
        },
        scopeDisclaimerText() {
            // A sent notification's recipients are history (the set as it stood then), so it gets its own wording.
            let key = 'labels.recipientScopeSentDisclaimer';
            if (!this.readOnly) {
                if (this.scopeAllActive) key = 'labels.recipientScopeAllActiveDisclaimer';
                else if (this.scopeActiveMembership) key = 'labels.recipientScopeActiveMembershipDisclaimer';
                else key = 'labels.recipientScopeMembershipDisclaimer';
                if (this.isMobile) key += 'Mobile';
            }
            return this.$LANG.Get(key);
        },
        subjectDisclaimerText() {
            // Substitute the sport center name into the subject shown in the disclaimer (drop the " - "
            // suffix entirely when the client has no sport center name set).
            let name = this.$store.state.user.client.sport_center_name || '';
            let suffix = name !== '' ? ' - ' + name : '';
            return this.$LANG.Get('labels.notificationSubjectDisclaimer').replace(' - %sport_center_name%', suffix);
        },
        personWontBeNotifiedText() {
            return this.$LANG.Get(this.isMobile ? 'labels.personWontBeNotifiedMobile' : 'labels.personWontBeNotified');
        },
        /**
         * Everything the reach preview depends on, as one value to watch.
         */
        reachRequest() {
            return JSON.stringify({
                channel: this.notification.channel,
                recipient_scope: this.recipientScope,
                recipient_membership_id: this.scopeMembership ? this.notification.recipient_membership_id : null,
                person_ids: this.usingScope ? [] : this.notification.persons.map((person) => person.person_id)
            });
        }
    },
    watch: {
        selectedPersonId(value) {
            if (value !== null) {
                this.addPerson(value);
            }
        },
        selectedGroupId(value) {
            if (value !== null) {
                this.addFrom('notifications/persons/from-group/' + value, () => {
                    this.selectedGroupId = null;
                });
            }
        },
        selectedSessionId(value) {
            if (value !== null) {
                this.addFrom('notifications/persons/from-session/' + value, () => {
                    this.selectedSessionId = null;
                });
            }
        },
        // The scopes are mutually exclusive: turning one on turns the others off.
        scopeAllActive(value) {
            if (value) {
                this.scopeActiveMembership = false;
                this.scopeMembership = false;
            }
        },
        scopeActiveMembership(value) {
            if (value) {
                this.scopeAllActive = false;
                this.scopeMembership = false;
            }
        },
        scopeMembership(value) {
            if (value) {
                this.scopeAllActive = false;
                this.scopeActiveMembership = false;
            } else {
                // The membership belongs to this scope alone, so it does not linger once the scope is off.
                this.notification.recipient_membership_id = null;
            }
        },
        reachRequest() {
            // Debounced: adding a whole group or ticking through the scopes should cost one request, not many.
            if (this.reachTimer !== null) {
                clearTimeout(this.reachTimer);
            }
            this.reachTimer = setTimeout(() => {
                this.reachTimer = null;
                this.loadReach();
            }, 400);
        }
    },
    methods: {
        personNotifiable(person) {
            return this.isMobile ? person.notifiable_mobile === true : person.notifiable === true;
        },
        mergePersons(persons) {
            for (let person of persons) {
                if (!this.notification.persons.some((existing) => existing.person_id === person.person_id)) {
                    this.notification.persons.push(person);
                }
            }
        },
        addPerson(personId) {
            let vm = this;
            this.$API.APICall('get', 'notifications/persons/single/' + personId, {}, (data, success) => {
                if (success) {
                    // Persons are always returned now, even when they cannot currently receive notifications;
                    // the table flags those with a warning icon.
                    vm.mergePersons(data.persons);
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
                // Reset the selection once the person has been added (mirrors the group/session behaviour).
                vm.selectedPersonId = null;
            });
        },
        addFrom(endpoint, reset) {
            let vm = this;
            this.$API.APICall('get', endpoint, {}, (data, success) => {
                if (success) {
                    vm.mergePersons(data.persons);
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
                reset();
            });
        },
        removePerson(index) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.generalRemovalQuestion'), callback: (response) => {
                    if (response) {
                        vm.notification.persons.splice(index, 1);
                    }
                }
            });
        },
        attachmentsUploaded(files) {
            for (let file of files) {
                this.notification.attachments.push({
                    name: file.name,
                    file: file
                });
            }
        },
        downloadAttachment(attachment) {
            window.open(attachment.file.link);
        },
        removeAttachment(index) {
            this.notification.attachments.splice(index, 1);
            if (this.notification.attachments.length === 0) {
                this.validation.attachments = '';
            }
        },
        backClick() {
            if (window.history.length === 1) {
                window.close();
            } else {
                this.$router.back();
            }
        },
        loadQuota() {
            let vm = this;
            this.$API.APICall('get', 'notifications/quota', {}, (data, success) => {
                if (success) {
                    vm.quota = data;
                }
            });
        },
        /**
         * How many of the selected people a mobile notification would reach right now. Only for an unsent mobile
         * notification; a membership scope without a membership picked has nothing to count yet.
         */
        loadReach() {
            if (!this.isMobile || this.readOnly || (this.scopeMembership && this.notification.recipient_membership_id === null)) {
                this.reach = null;
                return;
            }
            let vm = this;
            let request = this.reachRequest;
            this.$API.APICall('post', 'notifications/reach', JSON.parse(request), (data, success) => {
                // A slower answer to an older selection must not overwrite the current one.
                if (success && request === vm.reachRequest) {
                    vm.reach = data;
                }
            }, false);
        },
        /**
         * Plain text out of the rich-text editor's HTML: paragraphs and line breaks become new lines. Parsed with
         * DOMParser, which (unlike assigning innerHTML) never runs handlers inside the markup.
         */
        htmlToText(html) {
            if (typeof html !== 'string' || html === '') return '';
            let marked = html
                .replace(/<br\s*\/?>/gi, '\n')
                .replace(/<\/(p|div|li|h[1-6])>/gi, '\n');
            let text = new DOMParser().parseFromString(marked, 'text/html').body.textContent || '';
            return text.replace(/ /g, ' ').replace(/\n{3,}/g, '\n\n').trim();
        },
        /**
         * Rich-text editor HTML out of plain text: one paragraph per line, the text escaped.
         */
        textToHtml(text) {
            if (typeof text !== 'string' || text.trim() === '') return '';
            let escape = (value) => value
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
            return text.split('\n').map((line) => line.trim() === '' ? '<p><br></p>' : '<p>' + escape(line) + '</p>').join('');
        },
        /**
         * The characters in a value that cannot be saved: everything outside the Basic Multilingual Plane
         * (emoji and the like), which is four bytes in UTF-8 and more than the database connection can carry.
         */
        unsupportedCharacters(value) {
            if (typeof value !== 'string') return [];
            return value.match(/[\u{10000}-\u{10FFFF}]/gu) || [];
        },
        /**
         * Every unsupported character in the form, without duplicates, in the order they appear. Attachment
         * names are included: they are stored on the notification itself, so they fail the same way.
         */
        unsupportedCharactersInForm() {
            let values = [this.notification.name, this.notification.subject, this.notification.content];
            for (let attachment of this.notification.attachments) {
                values.push(attachment.name);
                values.push(attachment.file ? attachment.file.name : '');
            }
            let found = [];
            for (let value of values) {
                for (let character of this.unsupportedCharacters(value)) {
                    if (!found.includes(character)) found.push(character);
                }
            }
            return found;
        },
        saveClick() {
            // Sending immediately by e-mail is not allowed once the client is over their monthly limit.
            if (this.sendNowBlockedByLimit) {
                this.$LOCAL_BUS.TriggerEvent('popup', {type: 'alert', text: this.sendNowOverLimitText});
                return;
            }
            // Characters the server cannot store are listed rather than left to fail on save: they are easy to
            // paste in and impossible to spot in a long message.
            let unsupported = this.unsupportedCharactersInForm();
            if (unsupported.length > 0) {
                this.$LOCAL_BUS.TriggerEvent('popup', {
                    type: 'alert',
                    text: this.$LANG.Get('labels.unsupportedCharacters')
                        + '<div style="margin-top: 10px; font-size: 24px; letter-spacing: 6px;">'
                        + unsupported.join(' ') + '</div>'
                });
                return;
            }
            let valid = true;

            if (this.notification.name.trim() === '') {
                valid = false;
                this.validation.name = this.$LANG.Get('labels.mandatoryField');
            } else {
                this.validation.name = '';
            }

            // The send time is only required for an active, scheduled notification: it is hidden when sending
            // immediately (the server timestamps it) and optional for an inactive notification.
            if (!this.sendNow && this.notification.active && (this.sendDate === '' || this.sendDate === null || !/^\d{2}:\d{2}$/.test(this.sendTime))) {
                valid = false;
                this.validation.time = this.$LANG.Get('labels.mandatoryField');
            } else {
                this.validation.time = '';
            }

            // An e-mail subject is optional (empty means the default wording the disclaimer spells out); a mobile
            // notification's title is what the push and the app show, so it is required. Both are checked against
            // the column they are stored in.
            if (this.isMobile && this.notification.subject.trim() === '') {
                valid = false;
                this.validation.subject = this.$LANG.Get('labels.mandatoryField');
            } else if (this.notification.subject.length > 255) {
                valid = false;
                this.validation.subject = this.$LANG.Get(this.isMobile ? 'labels.titleTooLong' : 'labels.subjectTooLong');
            } else {
                this.validation.subject = '';
            }

            let content = this.isMobile
                ? this.notification.content
                : this.notification.content.replace(/<(.|\n)*?>/g, '');
            if (content.trim() === '') {
                valid = false;
                this.validation.content = this.$LANG.Get('labels.mandatoryField');
            } else {
                this.validation.content = '';
            }

            if (this.isMobile && this.notification.attachments.length > 0) {
                valid = false;
                this.validation.attachments = this.$LANG.Get('labels.mobileAttachmentsNotAllowed');
            } else {
                this.validation.attachments = '';
            }

            // The membership scope sends to the holders of one particular membership, so it needs one picked.
            if (this.scopeMembership && this.notification.recipient_membership_id === null) {
                valid = false;
                this.validation.recipient_membership_id = this.$LANG.Get('labels.mandatoryField');
            } else {
                this.validation.recipient_membership_id = '';
            }

            // An active (automatically sent) notification must have at least one recipient that can receive it.
            if (!this.sendNow && this.notification.active && !this.hasEligibleRecipient) {
                valid = false;
                this.validation.persons = this.$LANG.Get(this.isMobile ? 'labels.needEligibleRecipientMobile' : 'labels.needEligibleRecipient');
            } else {
                this.validation.persons = '';
            }

            if (!valid) return;

            // Sending immediately to nobody makes no sense: block it and explain why.
            if (this.sendNow && !this.hasEligibleRecipient) {
                this.$LOCAL_BUS.TriggerEvent('popup', {
                    type: 'alert',
                    text: this.$LANG.Get(this.isMobile ? 'alerts.sendNowNoRecipientsMobile' : 'alerts.sendNowNoRecipients')
                });
                return;
            }

            this.submitSave();
        },
        submitSave() {
            let payload = {
                name: this.notification.name,
                channel: this.notification.channel,
                subject: this.notification.subject,
                content: this.notification.content,
                attachments: this.isMobile ? [] : this.notification.attachments,
                active: this.notification.active,
                // A scope replaces the hand-picked list; the server resolves its persons at send time.
                person_ids: this.usingScope ? [] : this.notification.persons.map((person) => person.person_id),
                recipient_scope: this.recipientScope,
                recipient_membership_id: this.scopeMembership ? this.notification.recipient_membership_id : null,
                send_now: this.sendNow
            };
            if (!this.sendNow) {
                let hasTime = this.sendDate && /^\d{2}:\d{2}$/.test(this.sendTime);
                payload.time = hasTime ? (this.sendDate + ' ' + this.sendTime + ':00') : '';
            }

            let vm = this;
            let sendNow = this.sendNow;
            let method = (this.mode === 'add') ? 'post' : 'put';
            let action = (this.mode === 'add') ? 'notifications' : 'notifications/' + this.notification.id;
            this.$API.APICall(method, action, payload, (data, success) => {
                if (success) {
                    vm.$CROSS_TAB_BUS.TriggerEvent('reload-notifications', {});
                    vm.$LOCAL_BUS.TriggerEvent('notification', {
                        text: sendNow ? vm.$LANG.Get('alerts.notificationSent') : vm.$LANG.Get('alerts.recordSaved')
                    });
                    vm.backClick();
                } else if (data && data.error === 'limit_reached') {
                    vm.$LOCAL_BUS.TriggerEvent('popup', {type: 'alert', text: vm.sendNowOverLimitText});
                } else if (data && data.error === 'no_recipients') {
                    let text = vm.usingScope
                        ? vm.$LANG.Get('labels.recipientScopeEmpty')
                        : vm.$LANG.Get(vm.isMobile ? 'labels.needEligibleRecipientMobile' : 'labels.needEligibleRecipient');
                    vm.$LOCAL_BUS.TriggerEvent('popup', {type: 'alert', text: text});
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    },
    mounted() {
        this.$store.commit('selectMenu', 'clients');
        this.$store.commit('selectSubMenu', 'notifications');
        this.loadQuota();

        if (this.mode === 'add') {
            this.$store.commit('setBreadcrumbs', [
                {label: this.$LANG.Get('modules.clients'), link: null},
                {label: this.$LANG.Get('modules.notifications'), link: '/notifications'},
                {label: this.$LANG.Get('modules.newNotification'), link: null}
            ]);
            this.loaded = true;
        } else {
            let vm = this;
            this.$API.APICall('get', 'notifications/' + this.$route.params.id, {}, (data, success) => {
                if (success && data.notification) {
                    vm.notification.Fill(data.notification);
                    if (!Array.isArray(vm.notification.attachments)) vm.notification.attachments = [];
                    if (!Array.isArray(vm.notification.persons)) vm.notification.persons = [];
                    if (vm.notification.channel !== 'mobile') vm.notification.channel = 'email';
                    vm.scopeAllActive = vm.notification.recipient_scope === 'all_active';
                    vm.scopeActiveMembership = vm.notification.recipient_scope === 'active_membership';
                    vm.scopeMembership = vm.notification.recipient_scope === 'membership';
                    if (typeof vm.notification.time === 'string' && vm.notification.time.length >= 16) {
                        vm.sendDate = vm.notification.time.substring(0, 10);
                        vm.sendTime = vm.notification.time.substring(11, 16);
                    }
                    vm.$store.commit('setBreadcrumbs', [
                        {label: vm.$LANG.Get('modules.clients'), link: null},
                        {label: vm.$LANG.Get('modules.notifications'), link: '/notifications'},
                        {label: vm.notification.name, link: null}
                    ]);
                    vm.loaded = true;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    },
    unmounted() {
        if (this.reachTimer !== null) {
            clearTimeout(this.reachTimer);
        }
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.notification-disclaimer {
    margin: 0 0 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: $peach-wash;
    border: 1px solid $sand-300;
    color: $ink;
    font-size: 14px;
}

.notification-sent-banner {
    margin: 0 0 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: $success-surface;
    border: 1px solid $success;
    color: $ink-strong;
    font-size: 14px;
}

.form {
    margin-bottom: 10px;
    padding: 15px;
}

.notification-first-row {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
}

.form-float {
    float: left;
    margin-right: 10px;
    margin-bottom: 10px;
    padding: 15px;
    width: 320px;
}

.notification-half {
    width: 48%;
    float: left;
}

.notification-half:first-of-type {
    margin-right: 4%;
}

.notification-heading {
    margin: 0 0 10px;
}

.notification-active-field {
    margin-top: 10px;
}

.notification-sendnow-field {
    margin-top: 10px;
    margin-bottom: 10px;
}

.notification-sendnow-warning {
    margin-bottom: 10px;
    padding: 8px 10px;
    border-radius: 8px;
    background: $danger-surface;
    border: 1px solid $danger;
    color: $ink-strong;
    font-size: 13px;
}

.notification-sent-field {
    margin-top: 10px;
}

.sent-pill {
    display: inline-block;
    text-align: center;
    padding: 4px 10px;
    border-radius: 5px;
}

.sent-pill-yes {
    background: $success-badge;
}

.sent-pill-no {
    background: $sand-300;
}

.notification-recipients-card {
    max-width: 700px;
    margin-bottom: 10px;
    padding: 15px;
}

.notification-reach {
    margin: 0 0 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: $white;
    border: 1px solid $sand-300;
    color: $ink;
    font-size: 14px;
}

.notification-reach-title {
    font-weight: 600;
    margin-bottom: 6px;
}

.notification-reach-row {
    margin-bottom: 2px;
}

.notification-reach-number {
    display: inline-block;
    min-width: 36px;
    font-weight: 600;
    color: $ink-strong;
}

.notification-reach-note {
    margin-top: 6px;
    color: $ink-muted;
    font-size: 13px;
}

.notification-scope-row {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 12px;
}

.notification-scope-membership-row {
    margin-bottom: 10px;
}

.notification-scope-disclaimer {
    font-size: 13px;
}

.notification-attachments-disclaimer {
    font-size: 13px;
}

.notification-add-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.notification-add-field {
    flex: 1;
    min-width: 180px;
}

.notification-persons-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
}

.notification-persons-table td {
    padding: 4px 5px;
    border-bottom: 1px solid $sand-200;
}

.notification-persons-table-action {
    width: 20px;
    text-align: center;
}

.notification-persons-empty {
    color: $ink-muted;
    font-style: italic;
}

.notification-person-warning {
    margin-left: 6px;
    color: $danger;
}

.notification-person-sent {
    margin-left: 6px;
    color: $success;
}

.notification-button {
    cursor: pointer;
    color: $ink-muted;
}

.notification-button:hover {
    color: $danger;
}

.notification-content-card {
    max-width: 700px;
    margin-bottom: 10px;
    padding: 15px;
}

.notification-mobile-content :deep(textarea) {
    min-height: 220px;
    resize: vertical;
}

.notification-attachments {
    margin-top: 15px;
}

.notification-attachments-empty {
    color: $ink-muted;
    font-style: italic;
}

.attachment-row {
    display: flex;
    align-items: center;
    gap: 12px;
    border: 2px solid $sand-400;
    border-radius: 5px;
    padding: 8px 12px;
    margin-bottom: 8px;
    background: $white;
}

.attachment-row-icon {
    font-size: 22px;
    color: $orange-icon;
    flex-shrink: 0;
}

.attachment-row-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.attachment-row-actions {
    display: flex;
    gap: 12px;
    flex-shrink: 0;
}

.attachment-row-button {
    cursor: pointer;
    color: $ink-muted;
}

.attachment-row-button:hover {
    color: $ink-strong;
}

.attachment-row-button-delete:hover {
    color: $danger;
}
</style>
